import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkedAlt, FaBell, FaRobot, FaChartLine, FaRocket, FaFlag } from 'react-icons/fa';

const Roadmap = () => {
  // Memoize static data
  const milestones = useMemo(() => [
    {
      icon: <FaMapMarkedAlt className="text-3xl" />,
      title: 'Q1 2026',
      subtitle: 'Team Growth Made Simple',
      desc: 'Picture your small operation turning into a buzzing agency ,easily add team members to share leads, scores, and insights without any hassle.',
      color: 'from-purple-500 to-pink-500',
      bgColor: 'from-purple-50 to-pink-50',
      dotColor: 'bg-purple-500',
    },
    {
      icon: <FaBell className="text-3xl" />,
      title: 'Q2 2026',
      subtitle: 'Instant Lead Notifications',
      desc: 'Imagine getting a ping on your phone the moment a hot lead lands,plus smart tips on what to do next, so no opportunity slips away.',
      color: 'from-cyan-500 to-blue-500',
      bgColor: 'from-cyan-50 to-blue-50',
      dotColor: 'bg-cyan-500',
    },
    {
      icon: <FaRobot className="text-3xl" />,
      title: 'Q3 2026',
      subtitle: 'Smart Automation Boost',
      desc: 'Watch as your tool starts predicting customer moves and auto-sending follow-ups—turning everyday leads into steady sales wins.',
      color: 'from-orange-500 to-red-500',
      bgColor: 'from-orange-50 to-red-50',
      dotColor: 'bg-orange-500',
    },
  ], []);

  // Respect reduced motion
  const prefersReducedMotion = useMemo(() => 
    window.matchMedia('(prefers-reduced-motion: reduce)').matches, []
  );

  const containerVariants = {
    hidden: prefersReducedMotion ? { opacity: 1 } : { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  return (
    <section id="roadmap" className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-purple-50 py-32">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl [animation:css-pulse 4s ease-in-out infinite]" />
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-pink-200/30 rounded-full blur-3xl [animation:css-pulse 4s ease-in-out infinite] [animation-delay:1s]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-200/20 rounded-full blur-3xl [animation:css-pulse 4s ease-in-out infinite] [animation-delay:2s]" />
      </div>

      {/* Static grid overlay */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
            transform: 'translateZ(0)',
          }}
        />
      </div>

      <div className="relative container mx-auto px-8 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 30 }}
          animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.9 }}
            animate={prefersReducedMotion ? false : { opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-200 mb-6"
          >
            <FaRocket className="text-purple-600" />
            <span className="text-sm font-bold text-purple-900">Your Growth Story</span>
          </motion.div>

          <h2 className="text-5xl md:text-7xl font-black mb-6 leading-tight">
            <span className="block text-slate-900">The Path to</span>
            <span className="block bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-600 bg-clip-text text-transparent animate-gradient">
              Bigger Wins
            </span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Join us on this adventure: Starting as your personal lead whisperer, evolving into a full team ally that spots, scores, and seals more deals every day.
          </p>
        </motion.div>

        {/* Road-style Timeline */}
        <div className="relative">
          {/* Starting flag */}
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, x: -50 }}
            animate={prefersReducedMotion ? false : { opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="absolute -left-8 top-0 flex flex-col items-center will-change-transform"
            style={{ transform: 'translateZ(0)' }}
          >
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-2xl border-4 border-white">
              <FaFlag className="text-white text-xl" />
            </div>
            <div className="mt-3 text-sm font-bold text-slate-700 whitespace-nowrap">Your Start</div>
          </motion.div>

          {/* Main Road Path */}
          <div className="relative pl-12">
            {/* Road base - curved path */}
            <svg className="absolute left-0 top-0 w-full h-full" style={{ zIndex: 0 }}>
              <defs>
                <linearGradient id="roadGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" style={{ stopColor: '#a855f7', stopOpacity: 0.3 }} />
                  <stop offset="50%" style={{ stopColor: '#ec4899', stopOpacity: 0.3 }} />
                  <stop offset="100%" style={{ stopColor: '#06b6d4', stopOpacity: 0.3 }} />
                </linearGradient>
                <linearGradient id="roadStroke" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" style={{ stopColor: '#a855f7', stopOpacity: 1 }} />
                  <stop offset="50%" style={{ stopColor: '#ec4899', stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: '#06b6d4', stopOpacity: 1 }} />
                </linearGradient>
              </defs>
              <motion.path
                d="M 0 50 Q 200 20, 400 50 T 800 50 T 1200 50"
                fill="none"
                stroke="url(#roadStroke)"
                strokeWidth="8"
                strokeLinecap="round"
                initial={prefersReducedMotion ? false : { pathLength: 0 }}
                whileInView={prefersReducedMotion ? false : { pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 2, ease: "easeInOut" }}
                className="will-change-transform"
              />
              {/* Road dashes */}
              <motion.path
                d="M 0 50 Q 200 20, 400 50 T 800 50 T 1200 50"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="20 20"
                initial={prefersReducedMotion ? false : { pathLength: 0, strokeDashoffset: 100 }}
                whileInView={prefersReducedMotion ? false : { pathLength: 1, strokeDashoffset: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 2, ease: "easeInOut", delay: 0.3 }}
                className="will-change-transform"
              />
            </svg>

            {/* Milestone cards positioned along the road */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="relative grid grid-cols-1 md:grid-cols-3 gap-8 pt-32"
            >
              {milestones.map((milestone, idx) => (
                <motion.div
                  key={idx}
                  variants={itemVariants}
                  className="relative"
                >
                  {/* Connecting line to road */}
                  <motion.div
                    initial={prefersReducedMotion ? false : { height: 0 }}
                    animate={prefersReducedMotion ? false : { height: "8rem" }}
                    transition={{ delay: idx * 0.2 + 0.5, duration: 0.6 }}
                    className={`absolute top-0 left-1/2 -translate-x-1/2 w-1 -mt-32 bg-gradient-to-b ${milestone.color} rounded-full will-change-transform`}
                    style={{ zIndex: 1, transform: 'translateZ(0)' }}
                  />

                  {/* Milestone marker on road */}
                  <motion.div
                    initial={prefersReducedMotion ? false : { scale: 0 }}
                    animate={prefersReducedMotion ? false : { scale: 1 }}
                    transition={{ delay: idx * 0.2 + 0.3, type: "spring", stiffness: 300 }}
                    className="absolute -top-32 left-1/2 -translate-x-1/2 will-change-transform"
                    style={{ zIndex: 2, transform: 'translateZ(0)' }}
                  >
                    <motion.div
                      className={`w-12 h-12 rounded-full bg-gradient-to-br ${milestone.color} flex items-center justify-center shadow-2xl border-4 border-white will-change-transform`}
                      style={{ transform: 'translateZ(0)' }}
                      whileHover={prefersReducedMotion ? false : { scale: 1.3, rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      <div className={`w-3 h-3 rounded-full bg-white animate-pulse`} />
                    </motion.div>
                  </motion.div>

                  {/* Milestone Card */}
                  <motion.div
                    whileHover={prefersReducedMotion ? false : { y: -10, scale: 1.03 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="relative bg-white rounded-3xl p-8 shadow-xl border-2 border-transparent hover:border-purple-200 transition-all overflow-hidden group cursor-pointer will-change-transform"
                    style={{ transform: 'translateZ(0)' }}
                  >
                    {/* Gradient overlay on hover */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${milestone.bgColor} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl`} />
                    
                    {/* Content */}
                    <div className="relative z-10">
                      {/* Icon */}
                      <motion.div
                        className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${milestone.color} flex items-center justify-center mb-6 shadow-lg group-hover:shadow-2xl transition-shadow text-white will-change-transform`}
                        style={{ transform: 'translateZ(0)' }}
                        whileHover={prefersReducedMotion ? false : { rotate: [0, -10, 10, -10, 0] }}
                        transition={{ duration: 0.5 }}
                      >
                        {milestone.icon}
                      </motion.div>

                      {/* Quarter badge */}
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 mb-3">
                        <div className={`w-2 h-2 rounded-full ${milestone.dotColor} animate-pulse`} />
                        <span className="text-xs font-bold text-slate-700">{milestone.title}</span>
                      </div>

                      {/* Title */}
                      <h3 className="text-2xl font-black text-slate-900 mb-3 leading-tight">
                        {milestone.subtitle}
                      </h3>

                      {/* Description */}
                      <p className="text-slate-600 leading-relaxed mb-6">
                        {milestone.desc}
                      </p>

                      {/* Progress bar */}
                      <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden">
                        <motion.div
                          className={`absolute inset-y-0 left-0 bg-gradient-to-r ${milestone.color} rounded-full will-change-transform`}
                          style={{ transform: 'translateZ(0)' }}
                          initial={prefersReducedMotion ? false : { width: 0 }}
                          whileInView={prefersReducedMotion ? false : { width: "100%" }}
                          viewport={{ once: true }}
                          transition={{ delay: idx * 0.2 + 0.8, duration: 1, ease: "easeOut" }}
                        />
                      </div>

                      {/* Decorative elements */}
                      <div className={`absolute -bottom-4 -right-4 w-32 h-32 bg-gradient-to-br ${milestone.color} opacity-10 rounded-full blur-2xl group-hover:opacity-20 transition-opacity`} />
                    </div>

                    {/* Corner number badge */}
                    <motion.div
                      className={`absolute top-4 right-4 w-10 h-10 rounded-xl bg-gradient-to-br ${milestone.color} flex items-center justify-center shadow-lg text-white font-black text-lg will-change-transform`}
                      style={{ transform: 'translateZ(0)' }}
                      initial={prefersReducedMotion ? false : { rotate: -180, opacity: 0 }}
                      whileInView={prefersReducedMotion ? false : { rotate: 0, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.2 + 0.5, type: "spring" }}
                    >
                      {idx + 1}
                    </motion.div>
                  </motion.div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Finish flag */}
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, x: 50 }}
            animate={prefersReducedMotion ? false : { opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="absolute -right-8 top-0 flex flex-col items-center will-change-transform"
            style={{ transform: 'translateZ(0)' }}
          >
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-2xl border-4 border-white">
              <FaRocket className="text-white text-xl" />
            </div>
            <div className="mt-3 text-sm font-bold text-slate-700 whitespace-nowrap">Endless Possibilities</div>
          </motion.div>
        </div>

        {/* Bottom reference note */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
          animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-24 text-center"
        >
          <div className="inline-block bg-gradient-to-r from-purple-50 via-pink-50 to-cyan-50 p-6 rounded-2xl border border-purple-200 shadow-lg">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-8 h-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full" />
              <FaChartLine className="text-purple-600 text-xl" />
              <div className="w-8 h-1 bg-gradient-to-r from-pink-500 to-cyan-500 rounded-full" />
            </div>
            <p className="text-slate-600 text-sm font-medium">
              Built on trusted basics: Clear plans, reliable storage, smooth interfaces, and strong backends.
            </p>
          </div>
        </motion.div>

        {/* Floating road signs */}
        {[
          { text: "Grow", color: "purple", position: "top-1/4 left-1/4", delay: 0.5 },
          { text: "Notify", color: "pink", position: "top-1/3 right-1/4", delay: 0.7 },
          { text: "Automate", color: "cyan", position: "top-2/3 left-1/3", delay: 0.9 },
        ].map((sign, idx) => (
          <motion.div
            key={idx}
            initial={prefersReducedMotion ? false : { opacity: 0, scale: 0 }}
            animate={prefersReducedMotion ? false : { opacity: 1, scale: 1 }}
            transition={{ delay: sign.delay, type: "spring" }}
            className={`absolute ${sign.position} hidden xl:block`}
          >
            <motion.div
              animate={prefersReducedMotion ? false : { 
                y: [0, -10, 0],
                rotate: [-5, 5, -5]
              }}
              transition={{ 
                duration: 3, 
                repeat: Infinity, 
                ease: "easeInOut",
                delay: idx * 0.5 
              }}
              className={`px-4 py-2 rounded-xl bg-${sign.color}-100 border-2 border-${sign.color}-300 text-${sign.color}-700 font-bold text-sm shadow-lg will-change-transform`}
              style={{ transform: 'translateZ(0)' }}
            >
              {sign.text}
            </motion.div>
          </motion.div>
        ))}
      </div>

      <style>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s linear infinite;
        }
        @keyframes css-pulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>
    </section>
  );
};

export default Roadmap;